import { useNavigate, useParams } from 'react-router-dom';
import { Box, Breadcrumbs, FormControl, InputLabel, MenuItem, Modal, Pagination, Paper, Select, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import dayjs from '../../../../config/dayjs';

import { Container, MainContainer } from '../../../../components';
import { Menu } from '../Menu/Menu';
import RadiusButton from '../../../../components/RadiusButton';


import { Fragment, useContext, useEffect, useState } from 'react';
import { ProjectContext } from '../../../../providers/projectProvider';
import { getDataForCandidateShortlist } from '../../../../services/masterService';
import { useToaster } from '../../../../providers/ToasterProvider';

import ARROW from '../../../../assets/images/breadcrumb-arrow.svg';
import ARROW_OPEN from '../../../../assets/images/breadcrumb-arrow-open.svg';
import DELETE_ICON from '../../../../assets/images/action-delete.svg';
import ACTION_DELETE from '../../../../assets/images/action-delete.svg';
import { updateShortlistedCandidates, listShortlistedCandidates, removeShortListedCandidate, downloadShortlistCandidateTemplate } from '../../../../services/candidateService';

const columns = [
    { id: 'rollNo', label: 'Roll No', minWidth: 140 },
    { id: 'applicationNo', label: 'Application No', minWidth: 140 },
    { id: 'candidateName', label: 'Candidate Name', minWidth: 140 },
    { id: 'seatNo', label: 'Seat No', minWidth: 140 },
    { id: 'dob', label: 'DOB', minWidth: 140 },
    { id: 'actions', label: '', minWidth: 140 },
];

const errorColumns = [
    { id: 'index', label: 'Row Number', minWidth: 140 },
    { id: 'message', label: 'Error Message', minWidth: 220 },
];

const limit = parseInt(process.env.REACT_APP_PAGE_LIMIT);

export const CandidateShortlist = () => {

    const params = useParams();
    const { addNotification } = useToaster();

    const { projectDetails } = useContext(ProjectContext);

    const [shortlistedCandidates, setShortlistedCandidates] = useState([]);
    const [maxPages, setMaxPages] = useState(1);
    const [currentPage, setCurrentPage] = useState(1);

    const defaultDrive = { id: 0, driveName: 'None' };
    const defaultCourse = { id: 0, courseName: 'None' };

    const [drives, setDrives] = useState([defaultDrive]);
    const [selectedDrive, setSelectedDrive] = useState(defaultDrive);
    const [allCourses, setAllCourses] = useState([defaultCourse]);
    const [courses, setCourses] = useState([defaultCourse]);
    const [selectedCourse, setSelectedCourse] = useState(defaultCourse);
    const [allProjectMasters, setAllProjectMasters] = useState([]);
    const [projectMasters, setProjectMasters] = useState([]);
    const [selectedProjectMaster, setSelectedProjectMaster] = useState(null);

    const [uploadModalToggler, setUploadModalToggler] = useState(false);
    const [uploadResult, setUploadResult] = useState({
        isProcessed: false,
        updatedRecordsCount: 0,
        errors: []
    });
    const [candidateFile, setCandidateFile] = useState(null);
    const [dataUpdated, setDataUpdated] = useState(false);

    const handlePageChange = (e, value) => {
        setCurrentPage(value)
    };

    useEffect(() => {
        if (!selectedDrive?.id || !selectedCourse?.id || !selectedProjectMaster?.id) {
            console.log('Drive, Course or Project Master not selected');
            return;
        }
        const fetchCandidates = async () => {
            try {
                const body = {
                    driveId: selectedDrive.id,
                    courseId: selectedCourse.id,
                    limit: limit,
                    page: currentPage
                }
                const response = await listShortlistedCandidates(body);
                if (response.code === "OK") {
                    const totalCount = response.data.count;
                    const maxPage = Math.ceil(totalCount / limit);
                    setMaxPages(maxPage);
                    const data = response.data.rows ?? [];
                    setShortlistedCandidates(data.map((sCandidate) => {
                        return {
                            ...sCandidate,
                            dob: dayjs(sCandidate.dob).format('DD-MM-YYYY'),
                        };
                    }));
                }
            } catch (error) {
                const code = error?.response?.data?.code;
                console.log('There is an error while getting available data', code, error);
            }
        }
        fetchCandidates();
    }, [dataUpdated, currentPage]);

    useEffect(() => {
        const fetchRequiredData = async () => {
            try {
                const response = await getDataForCandidateShortlist();
                if (response.code === "OK") {
                    const data = response.data;
                    setDrives([defaultDrive, ...data.drives]);
                    setAllCourses(data.courses);
                    setAllProjectMasters(data.projectMasters);
                }
            } catch (error) {
                console.log('There is an error while getting available data', error);
            }
        }
        fetchRequiredData();
    }, []);

    useEffect(() => {
        const filteredCourses = [];
        if (selectedDrive?.id) {
            for (let pm of allProjectMasters) {
                if (pm.driveId === selectedDrive.id) {
                    const course = allCourses.find((course) => course.id === pm.courseId);
                    if (course && !filteredCourses.some((c) => c.id === course.id)) {
                        filteredCourses.push(course);
                    }
                }
            }
        }
        setCourses([defaultCourse, ...filteredCourses]);
        setSelectedCourse(defaultCourse);
        setProjectMasters([]);
        setSelectedProjectMaster();
    }, [selectedDrive]);

    const handleDriveChange = (driveId) => {
        const drive = drives.find((d) => d.id === driveId);
        setSelectedDrive(drive);
        setSelectedProjectMaster();
    }
    const handleCourseChange = (courseId) => {
        const course = courses.find((c) => c.id === courseId);
        setSelectedCourse(course);
        setSelectedProjectMaster();
    }
    const handleSelectProjectMaster = () => {
        if (selectedDrive.id === 0 || selectedCourse.id === 0) {
            return;
        }
        const projectMaster = allProjectMasters.find(pm => pm.driveId === selectedDrive.id && pm.courseId === selectedCourse.id);
        setSelectedProjectMaster(projectMaster);
        setDataUpdated(!dataUpdated);
    }
    const handleUploadFile = () => {
        if (!selectedDrive?.id || !selectedCourse?.id || !selectedProjectMaster?.id) {
            console.log('Drive, Course or Project Master not selected');
            return;
        }
        setUploadModalToggler(true);
    }
    const handleDownloadTemplate = async () => {
        try {
            const res = await downloadShortlistCandidateTemplate();
            const url = window.URL.createObjectURL(res);

            const link = document.createElement('a');
            link.href = url;
            link.setAttribute(
                'download',
                `shortlist-candidate-template.csv`,
            );
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
            addNotification('File downloaded successfully.', 'success');
        } catch (error) {
            console.log('There is an error while downloading shortlist candidate template', error);
        }
    }
    const handleFileChange = ({ target }) => {
        const file = target?.files[0];
        if (file && file?.type === 'text/csv') {
            setCandidateFile(file);
        }
    }
    const handleFileRemove = () => {
        const element = document.getElementById('candidateFile');
        if (element) {
            element.value = '';
            setCandidateFile(null);
        }
    }
    const handleUploadShortlistedCandidates = async () => {
        if (!selectedDrive?.id || !selectedCourse?.id || !selectedProjectMaster?.id) {
            addNotification('Please select drive and course.', 'error');
            return;
        }

        try {
            const body = new FormData();
            body.append('driveId', selectedDrive.id);
            body.append('courseId', selectedCourse.id);
            body.append('file', candidateFile);
            const res = await updateShortlistedCandidates(body);
            if (res.code === 'OK') {
                const errors = res.data.errors;
                const updatedCount = res.data.updatedCount;

                if (errors?.length) {
                    setUploadResult({
                        isProcessed: true,
                        updatedRecordsCount: updatedCount,
                        errors: errors
                    });
                } else {
                    setUploadResult({
                        isProcessed: true,
                        updatedRecordsCount: updatedCount,
                        errors: errors
                    });
                    setUploadModalToggler(false);
                    setDataUpdated(!dataUpdated);
                    addNotification('Candidates shortlisted successfully', 'success');
                }
            }
        } catch (error) {
            const code = error?.response?.data?.code;
            console.error('Error uploading shortlisted candidates', code, error);
        }
    }
    const handleRemoveShortlisted = async (rowIndex) => {
        const shortlistedCandidate = shortlistedCandidates[rowIndex];
        if (!shortlistedCandidate?.id) {
            console.log('Invalid candidate data');
            return;
        }
        try {
            const response = await removeShortListedCandidate(shortlistedCandidate.id);
            if (response.code === 'OK') {
                setShortlistedCandidates(shortlistedCandidates.filter((candidate) => candidate.id !== shortlistedCandidate.id));
                addNotification('Candidate removed successfully.', 'success');
            }
        } catch (error) {
            const code = error?.response?.data?.code;
            console.error('Error removing shortlisted candidate', code, error);
        }
    }

    const handleCloseUploadModal = () => {
        setUploadModalToggler(false);
        setCandidateFile(null);
        setUploadResult({
            isProcessed: false,
            updatedRecordsCount: 0,
            errors: []
        });
    }

    const renderTableCell = (columnId, rowIndex) => {
        const row = shortlistedCandidates[rowIndex];
        switch (columnId) {
            case 'rollNo':
                return row['rollNo'];
            case 'applicationNo':
                return row['applicationNo'];
            case 'candidateName':
                return row['candidateName'];
            case 'dob':
                return row['dob'];
            case 'candidateCount':
            case 'actions':
                return <Box component='span' sx={{
                    mx: 1,
                    cursor: 'pointer'
                }} onClick={() => handleRemoveShortlisted(rowIndex)}>
                    <img src={ACTION_DELETE} alt='' />
                    <Typography variant='body1' sx={{
                        display: 'inline-block',
                        verticalAlign: 'super',
                        ml: 1,
                        color: 'error.main'
                    }}>Remove</Typography>
                </Box>
            default:
                return row[columnId];
        }
    }

    const renderErrorTableCell = (columnId, rowIndex) => {
        const row = uploadResult.errors[rowIndex];
        console.log(columnId, rowIndex);
        switch (columnId) {
            case 'index':
                console.log('Row Index:', rowIndex);
                return row['index'];
            case 'rollNo':

                return row['rollNo']
            case 'message':
                console.log('Error Message:', row['message']);
                return row['message']
            default:
                return row[columnId];
        }
    }


    return (
        <MainContainer>
            <Menu>
                <Breadcrumbs aria-label="breadcrumb" separator='' sx={{ gap: 12 }}>
                    <Typography color="text.primary" sx={{ fontWeight: 400, textTransform: 'capitalize' }}>{projectDetails?.projectName}</Typography>
                    <Box sx={{ display: 'flex', alignContent: 'center' }}>
                        <img src={ARROW} alt=''></img>
                    </Box>

                    <Typography color="text.primary" sx={{ fontWeight: 500 }}>Candidate Shortlist</Typography>
                    <Box sx={{ display: 'flex', alignContent: 'center' }}>
                        <img src={ARROW_OPEN} alt='' ></img>
                    </Box>
                </Breadcrumbs>
            </Menu>
            <Container>
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mb: 2
                }}>
                    <Typography variant='h5' sx={{ m: 0 }}>
                        Candidate Shortlist
                    </Typography>
                    <RadiusButton
                        type='submit'
                        variant='contained'
                        color='secondary'
                        onClick={handleDownloadTemplate}
                    >
                        Download Template
                    </RadiusButton>
                </Box>

                <Box sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 2,
                    mb: 4,
                }}>
                    <FormControl className='form-field' sx={{ width: 222 }}>
                        <InputLabel id="driveName">Drive Name</InputLabel>
                        <Select
                            labelId="driveName"
                            id="drive"
                            label='Drive Name'
                            name='drive'
                            size='small'
                            value={selectedDrive?.id ?? 0}
                            onChange={(e, event) => handleDriveChange(event.props.value)}
                        >
                            {
                                drives.map(
                                    (drive) => <MenuItem key={drive.id} value={drive.id}>
                                        {drive.driveName}
                                    </MenuItem>
                                )
                            }
                        </Select>
                    </FormControl>
                    <FormControl className='form-field' sx={{ width: 222 }}>
                        <InputLabel id="courseName">Course Name</InputLabel>
                        <Select
                            labelId="courseName"
                            id="course"
                            label='Course Name'
                            name='course'
                            size='small'
                            value={selectedCourse?.id ?? 0}
                            onChange={(e, event) => handleCourseChange(event.props.value)}
                        >
                            {
                                courses.map(
                                    (course) => <MenuItem key={course.id} value={course.id}>
                                        {course.courseName}
                                    </MenuItem>
                                )
                            }
                        </Select>
                    </FormControl>
                    <RadiusButton
                        type='submit'
                        variant='contained'
                        color='secondary'
                        disabled={selectedDrive.id === 0 || selectedCourse.id === 0}
                        onClick={handleSelectProjectMaster}
                    >
                        Select
                    </RadiusButton>
                    <RadiusButton
                        type='submit'
                        variant='contained'
                        color='secondary'
                        disabled={!selectedDrive?.id || !selectedCourse?.id || !selectedProjectMaster?.id}
                        onClick={handleUploadFile}
                    >
                        Upload Shortlisted Candidates
                    </RadiusButton>
                </Box>

                <Paper sx={{
                    width: '100%',
                    overflow: 'hidden',
                    bgcolor: 'background.base',
                    maxHeight: 'calc(100vh - 305px)',
                    maxWidth: 'calc(100vw - 140px)',
                    borderRadius: 1,
                    mb: 4
                }}>
                    <TableContainer sx={{
                        px: 1,
                        pb: 1,
                        maxHeight: 'inherit',
                        maxWidth: 'inherit',
                        border: '1px solid',
                        borderColor: 'primary.main',
                        borderRadius: 1
                    }}>
                        <Table stickyHeader aria-label="sticky table">
                            <TableHead>
                                <TableRow>
                                    {columns.map((column) => (
                                        <TableCell
                                            sx={{
                                                color: 'primary.main'
                                            }}
                                            key={column.id}
                                            align='center'
                                            style={{ minWidth: column.minWidth }}
                                        >
                                            {column.label}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {!shortlistedCandidates?.length
                                    ? <TableRow sx={{ mx: 'auto', p: 2, color: 'grey.500' }}>
                                        <TableCell colSpan={5} sx={{ border: 'none', textAlign: 'center' }}>
                                            No records available
                                        </TableCell>
                                    </TableRow>
                                    : shortlistedCandidates.map((row, rowIndex) => {
                                        return (
                                            <TableRow hover={false} role="checkbox" key={rowIndex}>
                                                {columns.map((column, index) => {
                                                    return <TableCell key={column.id} align='center' sx={{
                                                        px: 1.5,
                                                        py: 0.75
                                                    }}>
                                                        {renderTableCell(column.id, rowIndex)}
                                                    </TableCell>
                                                })}
                                            </TableRow>
                                        );
                                    })
                                }
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>

                <Pagination sx={{ mt: 'auto' }} count={maxPages} page={currentPage} onChange={handlePageChange} shape="rounded" />


                <Modal open={uploadModalToggler} hideBackdrop={true} className='backdrop' onClose={handleCloseUploadModal}>
                    <Box className='modal-container' sx={{
                        maxWidth: 620,
                        p: 3,
                    }}>
                        <Typography variant='h6' sx={{
                            fontWeight: 400,
                            mb: 3,
                        }}>
                            Select Candidate File
                        </Typography>

                        <Box sx={{ mb: 3 }}>
                            <Box sx={{
                                overflow: 'auto',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                pb: 1
                            }}>
                                <RadiusButton
                                    variant='contained'
                                    component='label'
                                    htmlFor='candidateFile'
                                    color='secondary'
                                    sx={{ flexShrink: 0 }}
                                >
                                    Select File
                                </RadiusButton>
                                <input
                                    style={{ display: 'none' }}
                                    type='file'
                                    id='candidateFile'
                                    onChange={handleFileChange}
                                    accept='text/csv'
                                />
                                {
                                    candidateFile && candidateFile?.name && <Fragment>
                                        <Typography variant='subtitle1' >{candidateFile.name}</Typography>
                                        <img
                                            style={{ cursor: 'pointer' }}
                                            src={DELETE_ICON}
                                            onClick={() => handleFileRemove()}
                                            alt=''
                                        />
                                    </Fragment>
                                }
                            </Box>
                        </Box>

                        {uploadResult.isProcessed && <>
                            <Typography sx={{ fontSize: 17, mb: 2 }}>
                                Successfully updated records count: {!uploadResult.updatedRecordsCount ? 0 : uploadResult.updatedRecordsCount}
                            </Typography>
                            {uploadResult.errors?.length ? <>
                                <Typography sx={{ fontSize: 17, mb: 1 }}>Errors: </Typography>
                                <Paper sx={{
                                    width: '100%',
                                    overflow: 'hidden',
                                    bgcolor: 'background.base',
                                    maxHeight: 250,
                                    borderRadius: 1,
                                    mb: 4
                                }}>
                                    <TableContainer sx={{
                                        px: 1,
                                        pb: 1,
                                        maxHeight: 'inherit',
                                        maxWidth: 'inherit',
                                        border: '1px solid',
                                        borderColor: 'primary.main',
                                        borderRadius: 1
                                    }}>
                                        <Table stickyHeader aria-label="sticky table">
                                            <TableHead>
                                                <TableRow>
                                                    {errorColumns.map((column) => (
                                                        <TableCell
                                                            sx={{
                                                                color: 'primary.main',
                                                                p: 1,
                                                            }}
                                                            key={column.id}
                                                            align='center'
                                                            style={{ minWidth: column.minWidth }}
                                                        >
                                                            {column.label}
                                                        </TableCell>
                                                    ))}
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {
                                                    uploadResult.errors.map((row, rowIndex) => {
                                                        console.log('Row:', row);
                                                        return (
                                                            <TableRow hover={false} role="checkbox" key={rowIndex}>
                                                                {errorColumns.map((column) => {
                                                                    return <TableCell key={column.id} align='center' sx={{
                                                                        px: 1.5,
                                                                        py: 0.75
                                                                    }}>
                                                                        {renderErrorTableCell(column.id, rowIndex)}
                                                                    </TableCell>
                                                                })}
                                                            </TableRow>
                                                        );
                                                    })
                                                }
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </Paper>
                            </> : null}
                        </>}

                        <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-end',
                            gap: 2,
                        }}>
                            <RadiusButton variant='outlined' onClick={handleCloseUploadModal}>
                                Cancel
                            </RadiusButton>
                            <RadiusButton variant='contained' onClick={handleUploadShortlistedCandidates}>
                                Upload
                            </RadiusButton>

                        </Box>
                    </Box>
                </Modal>

            </Container>
        </MainContainer>
    );
}
